import AlternateEmail from "@mui/icons-material/AlternateEmail";
import ArrowBack from "@mui/icons-material/ArrowBack";
import GroupsOutlined from "@mui/icons-material/GroupsOutlined";
import HubOutlined from "@mui/icons-material/HubOutlined";
import TagOutlined from "@mui/icons-material/TagOutlined";
import {
  Alert,
  Button,
  ButtonBase,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { type FC, useEffect, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import type { UUID } from "../../../utils/uuid";
import { type ChannelName, isEmailChannel } from "../../channels/types";
import type { ConnectionCategory } from "../../moment/hooks/use-connections";
import type { MomentFormMoment } from "../../moment/types/moment-form-moment";
import { ConnectionSelectController } from "../../segment/components/connection-select-controller";
import { DynamicCCForm } from "../../segment/components/dynamic-cc-form";
import { SegmentForm } from "../../segment/components/segment-form";
import { SegmentSelectAutocomplete } from "../../segment/components/segment-select-autocomplete";
import { ChannelForm } from "../../segment/components/to-forms/channel-form";
import { NotifyForm } from "../../segment/components/to-forms/notify-form";
import { useSegmentSelector } from "../../segment/hooks/use-segment-selector";
import { useToTarget } from "../../to/use-to-target";

export type SurveyAlertWizardStep3Values = Pick<
  MomentFormMoment,
  | "target_manager"
  | "target_connection_type"
  | "target_channel"
  | "only_additional_recipients"
  | "notify_channel"
  | "additional_recipients"
  | "teams_channel_message_notify"
  | "channel"
  | "segment"
  | "cc_bcc"
>;

type SurveyAlertWizardStep3Props = {
  channel: Exclude<NonNullable<ChannelName>, "twilio" | "whatsapp">;
  connectionCategories: ConnectionCategory[];
  onCompleteChange: (complete: boolean) => void;
  onBack: () => void;
  restrictedSegments: UUID[];
};

const recipientOptions = {
  respondent: {
    label: "Each respondent receives a copy of their own answers",
    Icon: GroupsOutlined,
  },
  connection: {
    label: "A connection of the person who submitted the survey",
    Icon: HubOutlined,
  },
  person: { label: "A specific person", Icon: AlternateEmail },
  "slack-channel": { label: "A Slack Channel", Icon: TagOutlined },
  "teams-channel": { label: "A Teams Channel", Icon: TagOutlined },
} as const;

type RecipientOptionId = keyof typeof recipientOptions;

type CcBccFieldsProps = {
  channel: SurveyAlertWizardStep3Props["channel"];
};

const CcBccFields: FC<CcBccFieldsProps> = ({ channel }) => {
  const { control } = useFormContext<SurveyAlertWizardStep3Values>();
  const ccBcc = useWatch({ control, name: "cc_bcc" });
  const [visible, setVisible] = useState(false);

  if (!isEmailChannel(channel)) return null;

  return visible || Boolean(ccBcc?.cc.length || ccBcc?.bcc.length) ? (
    <DynamicCCForm disabled={false} initiallyOpen />
  ) : (
    <Button
      onClick={() => setVisible(true)}
      sx={{ alignSelf: "flex-start" }}
      data-analytics-id="survey-alert-wizard-add-cc-bcc"
    >
      Add CC/BCC
    </Button>
  );
};

const recipientOptionIdsByChannel: Record<
  SurveyAlertWizardStep3Props["channel"],
  RecipientOptionId[]
> = {
  slack: ["respondent", "connection", "person", "slack-channel"],
  work_email: ["respondent", "connection", "person"],
  personal_email: ["respondent", "connection"],
  teams: ["respondent", "connection", "person", "teams-channel"],
};

export const SurveyAlertWizardStep3: FC<SurveyAlertWizardStep3Props> = ({
  channel,
  connectionCategories,
  onCompleteChange,
  onBack,
  restrictedSegments,
}) => {
  const { control, setValue } = useFormContext<SurveyAlertWizardStep3Values>();
  const [targetChannelSelected, onlyAdditionalRecipients, additionalRecipients, segment] = useWatch(
    {
      control,
      name: ["target_channel", "only_additional_recipients", "additional_recipients", "segment"],
    },
  );
  const { toTarget } = useToTarget();
  const [segmentError, setSegmentError] = useState<string>();
  const segmentSelector = useSegmentSelector(setSegmentError);
  const selectedId: RecipientOptionId | null =
    toTarget === "employee"
      ? "respondent"
      : toTarget === "connection" || toTarget === "manager"
        ? "connection"
        : toTarget === "notify"
          ? "person"
          : channel === "slack" || channel === "teams"
            ? `${channel}-channel`
            : null;

  useEffect(() => {
    const requiresRecipients = targetChannelSelected || onlyAdditionalRecipients;

    onCompleteChange(Boolean(segment && (!requiresRecipients || additionalRecipients.length > 0)));
  }, [
    additionalRecipients,
    onCompleteChange,
    onlyAdditionalRecipients,
    segment,
    targetChannelSelected,
  ]);

  const handleSelect = (id: RecipientOptionId): void => {
    if (id === selectedId) return;

    const targetChannel = id === "slack-channel" || id === "teams-channel";

    setSegmentError(undefined);
    setValue("additional_recipients", []);
    setValue("segment", null);
    setValue("cc_bcc", { cc: [], bcc: [] });
    setValue("teams_channel_message_notify", false);
    setValue("target_manager", false);
    setValue("target_connection_type", id === "connection" ? "manager" : null);
    setValue("target_channel", targetChannel);
    setValue("only_additional_recipients", id === "person" || targetChannel);
    setValue("notify_channel", false);
  };

  return (
    <>
      <DialogTitle>
        <Stack spacing={1} alignItems="flex-start">
          <Button
            startIcon={<ArrowBack />}
            onClick={onBack}
            data-analytics-id="survey-alert-wizard-back"
          >
            Back
          </Button>
          <Typography variant="h6" component="span">
            Select Recipients
          </Typography>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3}>
          <Typography variant="body2">
            These people will be notified about each new survey submission.
          </Typography>
          <Stack spacing={2}>
            {recipientOptionIdsByChannel[channel].map((id) => {
              const { label, Icon } = recipientOptions[id];
              const selected = id === selectedId;

              return (
                <ButtonBase
                  key={id}
                  onClick={() => handleSelect(id)}
                  aria-pressed={selected}
                  data-analytics-id={`survey-alert-wizard-recipient-${id}`}
                  sx={{
                    width: "100%",
                    height: 56,
                    justifyContent: "flex-start",
                    gap: 1,
                    px: 2,
                    border: 1,
                    borderWidth: selected ? 3 : 1,
                    borderColor: selected ? "primary.main" : "divider",
                    borderRadius: 1,
                  }}
                >
                  <Icon sx={{ color: selected ? "primary.main" : "text.secondary" }} />
                  <Typography variant="body2">{label}</Typography>
                </ButtonBase>
              );
            })}
          </Stack>
          <Alert severity="info">
            Anyone completing this survey via its generic link will not trigger a notification, as
            we can’t determine which segment they belong to.
          </Alert>
          {selectedId === "respondent" && (
            <Stack spacing={1}>
              {segmentError ? (
                <Alert severity="error">{segmentError}</Alert>
              ) : (
                <SegmentSelectAutocomplete
                  restrictedSegments={restrictedSegments}
                  segmentSelectorInfo={segmentSelector}
                />
              )}
              <CcBccFields channel={channel} />
            </Stack>
          )}
          {selectedId === "connection" && (
            <Stack spacing={1}>
              <ConnectionSelectController connectionCategories={connectionCategories} />
              {segmentError ? (
                <Alert severity="error">{segmentError}</Alert>
              ) : (
                <SegmentSelectAutocomplete
                  restrictedSegments={restrictedSegments}
                  segmentSelectorInfo={segmentSelector}
                  label="About which Segment?"
                  placeholder="About which Segment?"
                  showConnectionHeading={false}
                />
              )}
              <CcBccFields channel={channel} />
            </Stack>
          )}
          {selectedId === "person" && (
            <NotifyForm
              formDisabled={false}
              restrictedSegments={restrictedSegments}
              continueClicked={false}
              showSegmentHeading={false}
              segmentLabel="About which Segment?"
              segmentPlaceholder="About which Segment?"
            />
          )}
          {(selectedId === "teams-channel" || selectedId === "slack-channel") && (
            <Stack spacing={1}>
              <ChannelForm
                formDisabled={false}
                restrictedSegments={restrictedSegments}
                continueClicked={false}
                showTargetOptions={false}
              />
              <SegmentForm
                connectionCategories={[]}
                disabled={false}
                showValidationErrors={false}
                restrictedSegments={restrictedSegments}
                segmentLabel="About which Segment?"
                segmentPlaceholder="About which Segment?"
              />
            </Stack>
          )}
        </Stack>
      </DialogContent>
    </>
  );
};
